import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { Language } from '@shared/types/language.type';

/**
 * A dropdown component for language selection.
 *
 * Displays the current language with a flag and provides a dropdown menu
 * to switch between available languages. Integrates with Transloco for
 * language switching functionality.
 *
 * @example
 * Basic usage:
 * ```html
 * <esignatur-language-dropdown />
 * ```
 */
@Component({
  selector: 'esignatur-language-dropdown',
  templateUrl: './esignatur-language-dropdown.html',
  styleUrl: './esignatur-language-dropdown.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class EsignaturLanguageDropdown {
  readonly #translocoService = inject(TranslocoService);
  readonly #isOpen = signal(false);

  readonly #languages: Language[] = [
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/us.png' },
    { code: 'da', name: 'Dansk', flag: 'https://flagcdn.com/w40/dk.png' },
    { code: 'no', name: 'Norsk', flag: 'https://flagcdn.com/w40/no.png' },
    { code: 'sv', name: 'Svenska', flag: 'https://flagcdn.com/w40/se.png' },
    { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w40/es.png' },
  ];

  readonly #activeLang = toSignal(this.#translocoService.langChanges$, {
    initialValue: this.#translocoService.getActiveLang(),
  });

  protected readonly currentLanguage = computed(() => {
    const activeLang = this.#activeLang();
    return this.#languages.find((lang) => lang.code === activeLang) || this.#languages[0];
  });

  protected readonly languages = this.#languages;
  protected readonly isOpen = this.#isOpen.asReadonly();

  protected toggleDropdown(): void {
    this.#isOpen.update((value) => !value);
  }

  protected closeDropdown(): void {
    this.#isOpen.set(false);
  }

  protected selectLanguage(languageCode: string): void {
    this.#translocoService.setActiveLang(languageCode);
    localStorage.setItem('translocoLang', languageCode);
    this.#isOpen.set(false);
  }
}
